'use client'

import { setNavStateContext } from '@/contexts/setNavState'
import type { MegaMenuType } from '@/frontend-types'
import { useBodyClip } from '@/hooks/useBodyClip'
import usePathnameWithSearch from '@/hooks/usePathnameWithSearch'
import { AnimatePresence } from 'motion/react'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import type { MegaMenuSideProps } from '../molecules/MegaMenuSide'
import { Header } from './Header'
import type { NavState } from './NavOverlay'
import { NavOverlay } from './NavOverlay'

export type HeaderWithNavOverlayProps = {
	megaMenu?: MegaMenuType
	megaMenuSide?: MegaMenuSideProps
}

export const HeaderWithNavOverlay: FC<HeaderWithNavOverlayProps> = ({ megaMenu, megaMenuSide }) => {
	const [state, setState] = useState<NavState>('closed')

	// Close when pathname changes
	const pathname = usePathnameWithSearch()
	useEffect(() => setState('closed'), [pathname])

	// Disable body scroll while nav overlay is open
	useBodyClip(state !== 'closed', 'nav-overlay')

	return (
		<setNavStateContext.Provider value={setState}>
			<Header state={state} onNavigate={setState} closeOnEsc />
			<AnimatePresence>
				{state !== 'closed' && (
					<NavOverlay
						state={state}
						megaMenu={megaMenu}
						megaMenuSide={megaMenuSide}
						onNavigate={setState}
					/>
				)}
			</AnimatePresence>
			{/* Cursed CSS hack to link the hover and active states of the two separate menu buttons */}
			<style jsx global>{`
				header[data-theme='white']:has(
						button[data-nav='menu']:hover,
						button[data-nav='menu']:active
					)
					button[data-nav='menu'] {
					color: var(--color-blue-shade-40);
				}
				header[data-theme='white']:has(button[data-nav='menu']:hover)
					button[data-nav='menu']
					.icon-circle {
					background-color: var(--color-blue-shade-40);
				}
				header[data-theme='white']:has(button[data-nav='menu']:active)
					button[data-nav='menu']
					.icon-circle {
					background-color: var(--color-blue-shade-70);
				}
			`}</style>
		</setNavStateContext.Provider>
	)
}
